/**
 * nbnTechnology.ts
 *
 * NBN technology-type alignment for service plans.
 *
 * Service plans don't (yet) all carry an explicit technology tag, but their
 * names/aliases usually encode one ("NBN Fixed Wireless Plus", "Home Fast
 * FTTP"). The Add/Edit service pickers need "selecting FTTP" to stop showing
 * Fixed Wireless / FTTN-only plans, so matching runs in three layers:
 *
 *   1. Explicit `plan.technology` array (schema field, admin-tagged) — wins.
 *   2. Name/alias/feature keyword detection (this file's patterns).
 *   3. Speed-cap fallback — a plan with no detectable technology is offered
 *      whenever its download speed fits the selected technology's max speed
 *      (the pre-existing behaviour, kept so generic plans never vanish).
 *
 * Pure functions — unit tested in src/utils/__tests__/nbnTechnology.test.ts.
 */

import type { ServicePlan } from '@/types/schema';

export type NbnTechnologyCode = 'FTTN' | 'FTTB' | 'FTTC' | 'FTTP' | 'FW' | 'HFC';

const TECH_PATTERNS: Array<{ code: NbnTechnologyCode; re: RegExp }> = [
  { code: 'FW', re: /fixed\s*wireless|\bFW\b/i },
  { code: 'FTTP', re: /\bFTTP\b|f(?:i|y)b(?:re|er)\s+to\s+the\s+prem/i },
  { code: 'FTTN', re: /\bFTTN\b|to\s+the\s+node/i },
  { code: 'FTTB', re: /\bFTTB\b|to\s+the\s+(?:building|basement)/i },
  { code: 'FTTC', re: /\bFTTC\b|to\s+the\s+(?:curb|kerb)/i },
  { code: 'HFC', re: /\bHFC\b|hybrid\s+f(?:i|y)b(?:re|er)/i },
];

type PlanLike = Pick<ServicePlan, 'planName' | 'displayName' | 'aliases' | 'features' | 'downloadSpeed' | 'technology'>;

/**
 * The technologies a plan is FOR. Explicit `plan.technology` tags win; else
 * keyword-detect from name/aliases/features. Empty array = generic plan
 * (works on any technology, subject to the speed cap).
 */
export function detectPlanTechnologies(plan: PlanLike): NbnTechnologyCode[] {
  const explicit = (plan.technology || [])
    .map((t) => String(t).toUpperCase().trim())
    .filter((t): t is NbnTechnologyCode => TECH_PATTERNS.some((p) => p.code === t));
  if (explicit.length > 0) return explicit;

  const haystack = [plan.planName, plan.displayName, ...(plan.aliases || []), ...(plan.features || [])]
    .filter(Boolean)
    .join(' \n ');
  return TECH_PATTERNS.filter((p) => p.re.test(haystack)).map((p) => p.code);
}

/** Parse "100 Mbps" / "1000 Mbps (1 Gbps)" → 100 / 1000. 0 when unparseable. */
export function parseDownloadSpeedMbps(downloadSpeed?: string): number {
  const m = downloadSpeed?.match(/^(\d+)/);
  return m ? parseInt(m[1], 10) : 0;
}

/**
 * Should this plan be offered when the given technology is selected?
 * - Technology-tagged plans only show for their own technology.
 * - Generic plans (no tag detectable) fall back to the speed cap.
 */
export function planMatchesNbnTechnology(
  plan: PlanLike,
  technology: NbnTechnologyCode | string,
  maxSpeedMbps: number,
): boolean {
  const tech = String(technology).toUpperCase() as NbnTechnologyCode;
  const declared = detectPlanTechnologies(plan);
  if (declared.length > 0) return declared.includes(tech);
  const speed = parseDownloadSpeedMbps(plan.downloadSpeed);
  return speed <= maxSpeedMbps;
}
